import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import type {
  NotificationItem,
  NotificationsListResult,
  NotificationsReadAllResult,
  NotificationsUnreadCount,
} from '../models/notifications.models';

type JsonRecord = Record<string, unknown>;

@Injectable({ providedIn: 'root' })
export class NotificationsApiService {
  private readonly http = inject(HttpClient);

  async getNotifications(params: { page: number; pageSize: number }): Promise<NotificationsListResult> {
    const page = Math.max(1, params.page);
    const pageSize = Math.min(100, Math.max(1, params.pageSize));
    const httpParams = new HttpParams().set('page', String(page)).set('pageSize', String(pageSize));

    const response = await firstValueFrom(
      this.http.get<unknown>('/api/notifications', { params: httpParams })
    );
    return this.mapListResponse(response, page, pageSize);
  }

  async getUnreadCount(): Promise<NotificationsUnreadCount> {
    const response = await firstValueFrom(this.http.get<unknown>('/api/notifications/unread-count'));
    const source = this.asRecord(response);
    const unreadCount = this.asNumber(
      source['unreadCount'] ?? source['UnreadCount'] ?? source['count'] ?? source['Count'],
      0
    );
    return { unreadCount: Math.max(0, Math.floor(unreadCount)) };
  }

  async markAsRead(id: string): Promise<void> {
    const url = `/api/notifications/${encodeURIComponent(id)}/read`;
    await firstValueFrom(this.http.patch(url, null));
  }

  async markAllAsRead(): Promise<NotificationsReadAllResult> {
    const response = await firstValueFrom(this.http.patch<unknown>('/api/notifications/read-all', null));
    const source = this.asRecord(response);
    const affectedCount = this.asNumber(source['affectedCount'] ?? source['AffectedCount'], 0);
    return { affectedCount: Math.max(0, Math.floor(affectedCount)) };
  }

  private mapListResponse(raw: unknown, fallbackPage: number, fallbackPageSize: number): NotificationsListResult {
    const source = this.asRecord(raw);
    const itemsRaw = Array.isArray(raw)
      ? raw
      : Array.isArray(source['items'])
        ? source['items']
        : Array.isArray(source['data'])
          ? source['data']
          : [];

    const items = itemsRaw.map((entry) => this.mapItem(entry)).filter((item) => !!item.id);
    const page = Math.max(1, this.asNumber(source['page'], fallbackPage));
    const pageSize = this.asNumber(source['pageSize'], fallbackPageSize);
    const totalCount = this.asNumber(source['totalCount'] ?? source['TotalCount'] ?? source['total'], 0);

    const hasMoreExplicit = source['hasMore'] ?? source['HasMore'];
    const hasMore =
      typeof hasMoreExplicit === 'boolean'
        ? hasMoreExplicit
        : totalCount > 0
          ? page * pageSize < totalCount
          : items.length >= pageSize;

    return {
      items,
      page,
      pageSize,
      totalCount,
      hasMore,
    };
  }

  private mapItem(raw: unknown): NotificationItem {
    const source = this.asRecord(raw);
    const createdAt = this.asString(source['createdAt']) || this.asString(source['CreatedAt']);
    return {
      id: this.asString(source['id']) || this.asString(source['Id']),
      title: this.asString(source['title']) || this.asString(source['name']) || 'Notification',
      message: this.asString(source['message']) || this.asString(source['description']) || '',
      type: this.normalizeNotificationType(source['type'] ?? source['Type']),
      occurredAt: createdAt || this.asString(source['occurredAt'] ?? source['OccurredAt']),
      readAt: this.asNullableString(source['readAt'] ?? source['ReadAt']),
      isRead: this.asBoolean(source['isRead'] ?? source['IsRead']),
      sourceUrl: this.asNullableString(source['sourceUrl'] ?? source['SourceUrl']),
      metadata: this.resolveNotificationMetadata(source),
    };
  }

  /**
   * API envia `type` como string PascalCase; mantém-se suporte a valor numérico legado (ex.: 5 = PlatformFeedbackReceived).
   */
  private normalizeNotificationType(value: unknown): string {
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) {
      const map: Record<number, string> = {
        5: 'PlatformFeedbackReceived',
      };
      const mapped = map[Math.trunc(value)];
      if (mapped) return mapped;
    }
    return 'SystemAlert';
  }

  /**
   * `metadataJson` vem como string JSON opcional; faz merge seguro com `metadata` objeto quando ambos existem.
   */
  private resolveNotificationMetadata(source: JsonRecord): Record<string, unknown> | null {
    const inline = this.asObjectOrNull(source['metadata'] ?? source['Metadata']);
    const fromJson = this.parseMetadataJsonString(
      source['metadataJson'] ?? source['MetadataJson'] ?? source['metadataJSON']
    );
    if (inline && fromJson) return { ...inline, ...fromJson };
    return fromJson ?? inline;
  }

  private parseMetadataJsonString(value: unknown): Record<string, unknown> | null {
    const str = this.asString(value).trim();
    if (!str) return null;
    try {
      const parsed: unknown = JSON.parse(str);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      /* ignora JSON inválido */
    }
    return null;
  }

  private asRecord(value: unknown): JsonRecord {
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as JsonRecord) : {};
  }

  private asString(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private asNullableString(value: unknown): string | null {
    const parsed = this.asString(value).trim();
    return parsed ? parsed : null;
  }

  private asBoolean(value: unknown): boolean {
    if (typeof value === 'boolean') return value;
    if (value === 1 || value === '1' || value === 'true') return true;
    return false;
  }

  private asNumber(value: unknown, fallback = 0): number {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim()) {
      const n = Number(value);
      if (Number.isFinite(n)) return n;
    }
    return fallback;
  }

  private asObjectOrNull(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : null;
  }
}
